import React, { useState, useRef, useEffect } from 'react';

export function Select({ 
  label,
  placeholder,
  value,
  onChange,
  options = [],
  fullWidth = false,
  disabled = false,
  required = false,
  error,
  helperText,
  size = 'medium',
  variant = 'outlined',
  name,
  className = '',
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setFocused(true);
    }
  };

  const handleSelect = (optionValue) => {
    const event = {
      target: {
        name,
        value: optionValue
      }
    };
    onChange?.(event);
    setIsOpen(false);
    setFocused(false);
  };

  const hasValue = value !== undefined && value !== null && value !== '';
  const showLabel = label && variant === 'outlined';
  const isStandard = variant === 'standard';

  // Find selected option
  const selectedOption = options.find(opt => 
    typeof opt === 'string' ? opt === value : opt.value === value
  );
  const displayValue = selectedOption 
    ? (typeof selectedOption === 'string' ? selectedOption : selectedOption.label)
    : '';

  // Size classes
  const sizeClasses = {
    small: 'text-sm px-3 py-2',
    medium: 'text-base px-4 py-3'
  };

  // Base select classes
  const selectBaseClasses = `
    relative w-full bg-white bg-opacity-5 text-white cursor-pointer
    border transition-all duration-200 ease-in-out
    focus:outline-none flex items-center justify-between
    disabled:opacity-50 disabled:cursor-not-allowed
    ${fullWidth ? 'w-full' : ''}
    ${sizeClasses[size]}
  `;

  // Variant-specific classes
  const variantClasses = {
    outlined: `
      border-gray-300 border-opacity-30 rounded-lg
      ${focused || isOpen ? 'border-purple-400 border-opacity-60 ring-2 ring-purple-400 ring-opacity-20' : ''}
      hover:border-opacity-50
    `,
    standard: `
      border-0 border-b border-gray-300 border-opacity-30 rounded-none
      ${focused || isOpen ? 'border-purple-400 border-opacity-80' : ''}
      hover:border-opacity-50 bg-transparent
      px-0 py-2
    `,
    filled: `
      border-gray-300 border-opacity-30 rounded-t-lg rounded-b-none border-b-2
      ${focused || isOpen ? 'border-purple-400 border-opacity-80' : ''}
      hover:border-opacity-50 bg-white bg-opacity-10
    `
  };

  const selectClasses = `${selectBaseClasses} ${variantClasses[variant]} ${className}`;

  const labelClasses = `
    absolute left-3 transition-all duration-200 ease-in-out pointer-events-none
    ${focused || hasValue || isOpen
      ? 'text-xs -top-2 bg-white bg-opacity-5 px-1 text-purple-300' 
      : 'text-sm top-3 text-gray-400'
    }
    ${isStandard ? 'left-0 bg-transparent' : ''}
    ${error ? 'text-red-400' : ''}
  `;

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={selectRef}>
      {/* Label for outlined variant */}
      {showLabel && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      
      {/* Select Button */}
      <div
        className={selectClasses}
        onClick={handleToggle}
        {...props}
      >
        <span className={`flex-1 text-left ${!hasValue ? 'text-gray-400' : ''}`}>
          {displayValue || (isStandard ? label : placeholder) || 'Select...'}
        </span>
        
        {/* Dropdown Arrow */}
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white bg-opacity-95 border border-gray-300 border-opacity-30 rounded-lg shadow-lg max-h-60 overflow-auto backdrop-blur-sm"
        >
          {options.map((option, index) => {
            const optionValue = typeof option === 'string' ? option : option.value;
            const optionLabel = typeof option === 'string' ? option : option.label;
            const isSelected = optionValue === value;
            
            return (
              <div
                key={index}
                className={`px-4 py-3 cursor-pointer transition-colors duration-150 text-gray-800
                  ${isSelected 
                    ? 'bg-purple-100 text-purple-800 font-medium' 
                    : 'hover:bg-gray-100'
                  }
                  ${index === 0 ? 'rounded-t-lg' : ''}
                  ${index === options.length - 1 ? 'rounded-b-lg' : ''}
                `}
                onClick={() => handleSelect(optionValue)}
              >
                {optionLabel}
              </div>
            );
          })}
        </div>
      )}

      {/* Helper text or error */}
      {(helperText || error) && (
        <div className={`mt-1 text-xs ${error ? 'text-red-400' : 'text-gray-400'}`}>
          {error || helperText}
        </div>
      )}
    </div>
  );
} 